import { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Shield, Heart, ChevronRight, LogOut, Megaphone, Users, Crown } from "lucide-react";

export default function GuardianDashboard() {
  const { user, loading, logout } = useAuth();
  const [data, setData] = useState(null);
  const [vault, setVault] = useState(null);

  const refresh = useCallback(() => {
    api.get("/guardian/dashboard").then((r) => setData(r.data)).catch((e) => console.error(e));
    api.get("/guardian/vault").then((r) => setVault(r.data)).catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    if (user && ["guardian", "admin"].includes(user.role)) refresh();
  }, [user, refresh]);

  if (loading) return <Layout><div className="px-6 py-32 text-white/40">Authorising…</div></Layout>;
  if (!user || !["guardian", "admin"].includes(user.role)) return <Navigate to="/admiral/login" replace />;
  if (!data) return <Layout><div className="px-6 py-32 text-white/40">Loading guardian bridge…</div></Layout>;

  return (
    <Layout>
      <section className="relative py-12 px-6 md:px-12 lg:px-20">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-2">
            <div>
              <span className="label-gold">Guardian Dashboard · {data.guardian.name}</span>
              <h1 data-testid="ashairah-flagship-title" className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-white mt-2">
                Ashairah <span className="gold-shimmer">Flagship</span> <span className="text-white/40">|</span> EIGOO Armada
              </h1>
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#C9A227] mt-2">
                {data.oversight_text}
              </p>
            </div>
            <button onClick={logout} className="btn-outline-gold text-[10px]" data-testid="guardian-logout-btn">
              <LogOut size={12} className="inline -mt-0.5 mr-1"/> Logout
            </button>
          </div>

          {/* Hero — Akanksha ↔ Ashairah */}
          <div className="glass border border-[#C9A227]/40 p-7 md:p-10 mt-8" style={{borderRadius:0}}>
            <div className="grid md:grid-cols-[1fr_auto_1fr] items-center gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="text-[#C9A227]" size={18}/>
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A227] font-bold">Guardian</span>
                </div>
                <div className="text-3xl md:text-4xl font-black uppercase tracking-tighter text-white">{data.guardian.name}</div>
                <div className="text-white/50 text-xs mt-1 truncate">{data.guardian.email}</div>
              </div>
              <div className="hidden md:block">
                <div className="w-32 h-px bg-gradient-to-r from-transparent via-[#C9A227] to-transparent"/>
                <div className="text-center text-[#C9A227] text-[10px] tracking-[0.3em] uppercase font-bold mt-2">Parental Link</div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 justify-end mb-2">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A227] font-bold">Heir</span>
                  <Heart className="text-[#C9A227]" size={18}/>
                </div>
                <div className="text-3xl md:text-4xl font-black uppercase tracking-tighter gold-shimmer">{data.heir.name}</div>
                <div className="text-white/50 text-xs mt-1">Ashairah Flagship</div>
              </div>
            </div>
          </div>

          {/* Heir Vault */}
          <div className="mt-8 glass border border-[#C9A227]/30 p-7" style={{borderRadius:0}}>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
              <h3 className="text-white text-xl font-black uppercase tracking-tight flex items-center gap-3">
                <Crown className="text-[#C9A227]"/> Heir Vault · {data.heir.name}
              </h3>
              <span className={`text-[10px] tracking-[0.3em] uppercase font-bold px-3 py-1.5 ${data.heir_vault.active ? "text-[#C9A227] border border-[#C9A227]" : "text-white/50 border border-white/20"}`}>
                {data.heir_vault.active ? "Mother's Designation · ACTIVE" : "Mother's Designation · Paused"}
              </span>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              <div className="border border-[#C9A227] p-5 bg-[#C9A227]/5">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A227] font-bold">Vault Balance</div>
                <div className="text-4xl font-black gold-shimmer tabular-nums mt-2">${data.heir_vault.balance.toFixed(2)}</div>
                <div className="text-[10px] tracking-widest uppercase text-white/40 mt-1">{data.heir_vault.ledger_count} entries</div>
              </div>
              <div className="border border-[#C9A227]/20 p-5">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A227] font-bold">Pacific Siren · Submitted</div>
                <div className="text-4xl font-black text-white tabular-nums mt-2">{data.platform.submitted_admiral_candidates}</div>
                <div className="text-[10px] tracking-widest uppercase text-white/40 mt-1">Admiral Candidates</div>
              </div>
              <div className="border border-[#C9A227]/20 p-5">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A227] font-bold">Marketing Posture</div>
                <div className="text-2xl font-black text-white mt-2">
                  <span className="tabular-nums">{data.platform.campaigns}</span> <span className="text-white/40 text-sm">campaigns</span>
                </div>
                <div className="text-2xl font-black text-white mt-1">
                  <span className="tabular-nums">{data.platform.leads}</span> <span className="text-white/40 text-sm">leads</span>
                </div>
              </div>
            </div>
          </div>

          {/* Ledger */}
          {vault && (
            <div className="mt-8">
              <div className="flex items-center gap-3 mb-4">
                <Heart className="text-[#C9A227]"/>
                <h3 className="text-white text-lg font-black uppercase tracking-tight">Heir Vault Ledger</h3>
              </div>
              {vault.ledger.length === 0 ? (
                <p className="text-white/40 py-6 text-center text-sm border border-[#C9A227]/10">No transactions in the vault yet.</p>
              ) : (
                <div className="space-y-2">
                  {vault.ledger.map((entry) => (
                    <div key={entry.id} className="glass border border-[#C9A227]/15 p-4 grid grid-cols-3 gap-3 text-sm items-center">
                      <div className="text-[#C9A227] text-[10px] tracking-widest uppercase font-bold">{entry.source}</div>
                      <div className="text-white tabular-nums font-bold">+${entry.amount.toFixed(2)}</div>
                      <div className="text-white/40 text-xs text-right">{entry.created_at?.slice(0, 19).replace("T", " ")}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Quick links */}
          <div className="grid md:grid-cols-2 gap-5 mt-8">
            {user.role === "admin" && (
              <Link to="/admiral" className="glass border border-[#C9A227]/20 hover:border-[#C9A227]/60 transition-colors p-6 block">
                <Megaphone className="text-[#C9A227]"/>
                <h4 className="text-white text-lg font-black uppercase tracking-tight mt-3">Marketing Hub</h4>
                <p className="text-white/55 text-xs mt-2">Campaign Dashboard, Lead Tracker, Social Signal Monitor.</p>
                <span className="text-[#C9A227] text-[10px] tracking-[0.2em] uppercase font-bold mt-3 inline-block">Open <ChevronRight size={12} className="inline -mt-0.5"/></span>
              </Link>
            )}
            <Link to="/leaderboard" className="glass border border-[#C9A227]/20 hover:border-[#C9A227]/60 transition-colors p-6 block">
              <Users className="text-[#C9A227]"/>
              <h4 className="text-white text-lg font-black uppercase tracking-tight mt-3">Search for the Siren · Live</h4>
              <p className="text-white/55 text-xs mt-2">Public leaderboard for the Admiral casting call.</p>
              <span className="text-[#C9A227] text-[10px] tracking-[0.2em] uppercase font-bold mt-3 inline-block">View <ChevronRight size={12} className="inline -mt-0.5"/></span>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
